import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  UserIcon,
  CogIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useCorrelationSearchLazyQuery } from '../types/generated';

interface CorrelationSearchProps {
  value: string;
  onChange: (value: string) => void;
}

const CorrelationSearch: React.FC<CorrelationSearchProps> = ({ value, onChange }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use real data from GraphQL
  const [searchCorrelations, { data: searchData, loading }] = useCorrelationSearchLazyQuery();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Trigger search when input changes
  useEffect(() => {
    if (value.length >= 2) {
      const searchTerm = `%${value}%`;

      // Check if search contains colon (table:id format)
      const colonIndex = value.indexOf(':');
      const hasColon = colonIndex > 0;

      let variables: any = { searchTerm };

      if (hasColon) {
        // Extract table and id parts
        const tablePart = value.substring(0, colonIndex);
        const idPart = value.substring(colonIndex + 1);

        variables = {
          searchTerm,
          hasColon: true,
          tablePart: `%${tablePart}%`,
          idPart: `%${idPart}%`
        };
      }

      searchCorrelations({ variables });
    }
  }, [value, searchCorrelations]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(newValue.length >= 2);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (value.length >= 2) {
      setShowSuggestions(true);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  const clearSearch = () => {
    onChange('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <div className="w-2 h-2 bg-green-500 rounded-full" />;
      case 'failed':
        return <div className="w-2 h-2 bg-red-500 rounded-full" />;
      case 'running':
        return <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />;
      default:
        return <div className="w-2 h-2 bg-gray-400 rounded-full" />;
    }
  };

  // Get suggestions from real GraphQL data
  const suggestions = searchData?.invocations || [];

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div
        className={`
          relative flex items-center border rounded-lg transition-all duration-200
          ${isFocused
            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
            : 'border-gray-300 dark:border-gray-600'
          }
          bg-white dark:bg-gray-700
        `}
      >
        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 ml-3 flex-shrink-0" />

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder="Search by correlation ID, function, record (table:id), or event..."
          className="flex-1 px-3 py-2 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 border-none outline-none"
        />

        {value && (
          <button
            onClick={clearSearch}
            className="p-1 mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Suggestions Dropdown */}
      <AnimatePresence>
        {showSuggestions && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-80 overflow-auto"
          >
            {loading && value.length >= 2 && (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Searching...
              </div>
            )}

            {!loading && suggestions.length === 0 && value.length >= 2 && (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No results found for "{value}"
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="py-2">
                <div className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                  Recent Matches
                </div>
                {suggestions.map((suggestion, index) => (
                  <motion.button
                    key={suggestion.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => handleSuggestionClick(suggestion.correlation_id || suggestion.source_user_email || suggestion.source_function)}
                    className="w-full px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-gray-50 dark:border-gray-700/50 last:border-b-0"
                  >
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(suggestion.status)}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          {suggestion.correlation_id && (
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono truncate">
                              {suggestion.correlation_id}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          {suggestion.source_function && (
                            <div className="flex items-center space-x-1">
                              <CogIcon className="h-3 w-3" />
                              <span>{suggestion.source_function}</span>
                            </div>
                          )}

                          {suggestion.source_user_email && (
                            <div className="flex items-center space-x-1">
                              <UserIcon className="h-3 w-3" />
                              <span className="truncate">{suggestion.source_user_email}</span>
                            </div>
                          )}

                          {(suggestion.source_event_payload as any)?.event?.data?.new?.id && suggestion.source_table && (
                            <div className="flex items-center space-x-1">
                              <span className="font-mono text-xs">
                                {suggestion.source_table.split('.')[1]}:{(suggestion.source_event_payload as any).event.data.new.id}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center space-x-1">
                            <ClockIcon className="h-3 w-3" />
                            <span>{new Date(suggestion.created_at).toLocaleTimeString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            )}

            {/* Search Tips */}
            {value.length < 2 && (
              <div className="p-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="space-y-2">
                  <p className="font-medium">Search Tips:</p>
                  <ul className="space-y-1 ml-2">
                    <li>• Type at least 2 characters to search</li>
                    <li>• Search by correlation ID, user email, function name, or record ID</li>
                    <li>• Use table:id format (e.g., moves:41885) to search by specific record</li>
                    <li>• Use partial matches to find related invocations</li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CorrelationSearch;